import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, History, XCircle, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export type ValidationAnswerTone =
  | "recorded"
  | "recordedEarlier"
  | "unknownCode"
  | "lookupFailed"
  | "cancelled"
  | "notConfirmed";

interface ToneStyle {
  icon: LucideIcon;
  panel: string;
  mark: string;
  live: "status" | "alert";
}

/** Every answer this screen gives is read across a counter with somebody
 *  waiting, so each tone carries its own word and its own mark — the tint is
 *  the last of the three to arrive and the first to be lost in greyscale. */
const tones: Record<ValidationAnswerTone, ToneStyle> = {
  recorded: {
    icon: CheckCircle2,
    panel: "border-success/40 bg-success/10",
    mark: "text-success",
    live: "status",
  },
  recordedEarlier: {
    icon: History,
    panel: "border-primary/40 bg-primary/10",
    mark: "text-foreground",
    live: "status",
  },
  unknownCode: {
    icon: XCircle,
    panel: "border-destructive/40 bg-destructive/10",
    mark: "text-destructive",
    live: "alert",
  },
  lookupFailed: {
    icon: AlertTriangle,
    panel: "border-border bg-muted",
    mark: "text-foreground",
    live: "alert",
  },
  cancelled: {
    icon: XCircle,
    panel: "border-destructive/40 bg-destructive/10",
    mark: "text-destructive",
    live: "alert",
  },
  notConfirmed: {
    icon: AlertTriangle,
    panel: "border-border bg-muted",
    mark: "text-foreground",
    live: "alert",
  },
};

interface ValidationAnswerProps {
  tone: ValidationAnswerTone;
  headline: string;
  children?: ReactNode;
}

export function ValidationAnswer({ tone, headline, children }: ValidationAnswerProps) {
  const { icon: Icon, panel, mark, live } = tones[tone];

  return (
    <div role={live} className={cn("flex items-start gap-4 rounded-lg border p-5 sm:p-6", panel)}>
      <Icon className={cn("mt-1 h-7 w-7 shrink-0 sm:h-8 sm:w-8", mark)} aria-hidden="true" />
      <div className="min-w-0 space-y-2">
        <p
          className={cn(
            "font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl",
            mark,
          )}
        >
          {headline}
        </p>
        {children && <div className="space-y-1 text-base text-foreground">{children}</div>}
      </div>
    </div>
  );
}
